import apiClient from "@/app/api/client";
import type {
	ReportAnalytics,
	ReportDescriptorView,
	ReportExportFormat,
	ReportExportJob,
	ReportRowsResult,
	ReportSummary,
} from "../types";

interface SingleResponse<T> {
	success: true;
	data: T;
}

export const reportsApi = {
	async listDescriptors(): Promise<ReportDescriptorView[]> {
		const response =
			await apiClient.get<SingleResponse<{ items: ReportDescriptorView[] }>>("/reports");

		return response.data.data.items;
	},

	/**
	 * ⚠️ Ustunlar SERVERDAN keladi (`data.columns`) — jadval ularni
	 * qattiq kodlamaydi, ya'ni yangi hisobot qo'shilganda UI
	 * o'zgarmaydi.
	 */
	async rows(
		kind: string,
		params: Record<string, string>,
		page: number,
		limit: number
	): Promise<ReportRowsResult> {
		const response = await apiClient.get<SingleResponse<ReportRowsResult>>(`/reports/${kind}`, {
			params: { ...params, page, limit },
		});

		return response.data.data;
	},

	/**
	 * ⚠️ `buildReportParams` QAYTA ISHLATILADI, o'z nusxasi
	 * QURILMAYDI — `useReportFilters.ts` dagi «jadval, eksport va chop
	 * etish uchalasi bitta funksiyadan o'tadi» qoidasi endi TO'RTTA
	 * iste'molchiga tegishli. Nusxa qurilsa «kartalarda bo'lim filtri
	 * ishlamaydi» sinfidagi jimgina tafovut paydo bo'lardi.
	 */
	async summary(kind: string, params: Record<string, string>): Promise<ReportSummary> {
		const response = await apiClient.get<SingleResponse<ReportSummary>>(
			`/reports/${kind}/summary`,
			{ params }
		);

		return response.data.data;
	},

	/**
	 * Analitika konverti (`GET /reports/{kind}/analytics`, spec §2.3).
	 * Parametrlar `buildReportParams` dan — `summary()` bilan bir xil qoida.
	 * `matrixLimit`/`trendMonths` uzatilmaydi — server standartlari (14 / 12)
	 * ishlaydi.
	 */
	async analytics(kind: string, params: Record<string, string>): Promise<ReportAnalytics> {
		const response = await apiClient.get<SingleResponse<ReportAnalytics>>(
			`/reports/${kind}/analytics`,
			{ params }
		);

		return response.data.data;
	},

	async createExport(
		kind: string,
		params: Record<string, string>,
		format: ReportExportFormat
	): Promise<ReportExportJob> {
		const response = await apiClient.post<SingleResponse<ReportExportJob>>(
			`/reports/${kind}/exports`,
			{ ...params, format }
		);

		return response.data.data;
	},

	/**
	 * O'z eksportlarim — oxirgi 7 kun, yangisi oldinda. `limit`
	 * serverda 1..20 (standart 10).
	 */
	async listExports(limit?: number): Promise<ReportExportJob[]> {
		const response = await apiClient.get<SingleResponse<{ items: ReportExportJob[] }>>(
			"/reports/exports",
			{ params: limit === undefined ? undefined : { limit } }
		);

		return response.data.data.items;
	},

	async exportStatus(id: string): Promise<ReportExportJob> {
		const response = await apiClient.get<SingleResponse<ReportExportJob>>(`/reports/exports/${id}`);

		return response.data.data;
	},

	/**
	 * ⚠️ **Yuklab olish `apiClient` ORQALI, oddiy havola bilan EMAS.**
	 * `GET /reports/exports/{id}/download` `authMiddleware` bilan
	 * himoyalangan va access token faqat JS xotirasida (CLAUDE.md auth
	 * modeli) — `<a href>` yoki `window.open()` unga Bearer sarlavhasini
	 * qo'sha olmaydi va 401 qaytarardi.
	 *
	 * ⚠️ **Xom `fetch` ham EMAS** (reja Task 6 dagi variant): u
	 * `baseURL` ni ham, 401 dagi bitta-uchuvchi refresh
	 * interceptorini ham chetlab o'tardi — token muddati tugagan
	 * paytda yuklash jimgina yiqilardi. `responseType: "blob"`
	 * javobni JSON deb o'qishning oldini oladi (`leadsService.download`
	 * naqshi).
	 */
	async download(id: string): Promise<{ blob: Blob; filename: string | null }> {
		const response = await apiClient.get<Blob>(`/reports/exports/${id}/download`, {
			responseType: "blob",
		});

		return {
			blob: response.data,
			filename: parseFilename(response.headers["content-disposition"]),
		};
	},
};

/**
 * `Content-Disposition` dan fayl nomini ajratadi.
 *
 * ⚠️ Nom SERVERDAN olinadi, mijozda qayta QURILMAYDI: uni worker
 * `‹kind›-‹dateFrom›-‹dateTo›.‹ext›` shaklida yozadi va sarlavha shu
 * nomni olib keladi. Mijozda qayta qurish ikki nomni ajratardi —
 * eksport SO'RALGAN paytdagi filtrlar bilan yuklab olingan paytdagi
 * filtrlar bir xil bo'lishi shart emas (foydalanuvchi oraliqni
 * o'zgartirgan bo'lishi mumkin), ya'ni fayl ichidagi ma'lumot bilan
 * nomi jimgina ajralib ketardi.
 *
 * `null` — sarlavha yo'q yoki tanib bo'lmadi; chaqiruvchi o'z zaxira
 * nomini qo'yadi.
 */
function parseFilename(header: unknown): string | null {
	if (typeof header !== "string") {
		return null;
	}

	return header.match(/filename="([^"]+)"/)?.[1] ?? null;
}
